import type { KeyboardEvent } from "react";
import SntSearchIcon from "@icons/SntSearchIcon";
import SntCloseSmallIcon from "@icons/SntCloseSmallIcon";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@hooks/useLanguage";

interface SidebarMenuSearchProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  /** Enter with a non-empty query: open the first visible match. */
  onEnter: () => void;
}

/**
 * The search field above the admin menu (STIC-15790 follow-up).
 *
 * Filters on every keystroke — the tree is already in memory, so there is
 * nothing to debounce (which rules out reusing SntSearch: its 500ms delay and
 * submit button are built for server round-trips). Rendered by SidebarBody
 * outside the scroll area so it never scrolls away.
 */
const SidebarMenuSearch = ({ value, onChange, onClear, onEnter }: SidebarMenuSearchProps) => {
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClear();
    } else if (e.key === "Enter" && value.trim() !== "") {
      e.preventDefault();
      onEnter();
    }
  };

  return (
    <div className="sidebar-menu-search snt-search-box">
      <div className="sidebar-menu-search-field">
        <SntSearchIcon size={14} className="sidebar-menu-search-icon" />
        <input
          type="text"
          className="form-control"
          value={value}
          placeholder={translate(LanguageKey.find_in_menu_key)}
          aria-label={translate(LanguageKey.find_in_menu_key)}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
        />
        {value !== "" && (
          <button
            type="button"
            className="sidebar-menu-search-clear"
            title={translate(LanguageKey.clear_key)}
            aria-label={translate(LanguageKey.clear_key)}
            onClick={onClear}
          >
            <SntCloseSmallIcon size={12} />
          </button>
        )}
      </div>
    </div>
  );
};

export default SidebarMenuSearch;
